// src/components/Login.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn } from '../utils/authUtils';
import { TextField } from '@mui/material';

function Login() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoading(true); setError(null);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: 'var(--bg)',
    }}>
      {/* ── Left panel ── */}
      <div style={{
        flex: '0 0 44%',
        background: 'var(--text-primary)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '40px 48px',
      }}
        className="auth-left-panel"
      >
        {/* Background track image */}
        <img src="/model3-track-action.jpg" alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.22, objectPosition: 'center 40%' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(25,130,206,0.18) 0%, transparent 60%), linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)' }} />

        {/* Logo */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <img src="/M3CLogo.png" alt="Model 3 Challenge" style={{ height: 36, width: 'auto', filter: 'brightness(0) invert(1)' }} />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: '#1982ce', letterSpacing: '0.04em', marginTop: 4 }}>2026</div>
        </div>

        {/* Bottom copy */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', color: '#fff', lineHeight: 1.05, letterSpacing: '0.03em', marginBottom: 14, fontWeight: 400 }}>
            The World's<br />Electric Time<br />Attack Series
          </div>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: 1.7, maxWidth: 300 }}>
            Compete at your local track. Earn points worldwide. Tesla Model 3 &amp; Model Y only.
          </p>
          {/* MPP credit */}
          <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>Presented by</span>
<span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', fontWeight: 700, letterSpacing: '0.02em' }}>Mountain Pass Performance</span>
          </div>
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 32px',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2.4rem', letterSpacing: '0.03em', color: 'var(--text-primary)', lineHeight: 1, fontWeight: 400, marginBottom: 8 }}>
              Welcome Back
            </h1>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>Sign in to your account to continue</p>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background: 'rgba(255,59,48,0.08)', border: '1px solid rgba(255,59,48,0.2)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 20, fontSize: '0.85rem', color: 'var(--red)' }}>
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <TextField
              label="Email" type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth size="small" required
              autoComplete="email"
            />
            <TextField
              label="Password" type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth size="small" required
              autoComplete="current-password"
            />

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 6,
                padding: '12px',
                background: loading ? 'var(--text-tertiary)' : 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-body)',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background var(--transition)',
                letterSpacing: '0.01em',
              }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {/* Footer link */}
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Responsive: hide left panel on small screens */}
      <style>{`
        @media (max-width: 700px) {
          .auth-left-panel { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default Login;
